import { useEffect, useMemo, useState, type FormEvent } from "react";

import {
  MAIN_OCCUPATIONS,
  MORE_OCCUPATIONS,
  OCCUPATIONS,
  occupationLabel,
  type Occupation,
} from "@core";
import { type EmployerView } from "../lib/employer";
import { addEmployee, mayAddPeople, type AddResult } from "@/features/employer-people";

/**
 * The people a household employs, and adding somebody to it.
 *
 * The hours, overtime and wages are computed by src/core — the same code the
 * app runs — and adding somebody goes through src/features/employer-people,
 * which calls the same `add_employee` function the app calls. Nothing about
 * who may do what is decided here: the database refuses, and this screen
 * reports the refusal in words.
 */

const ALL_OCCUPATIONS: Occupation[] = [...MAIN_OCCUPATIONS, ...MORE_OCCUPATIONS];

const REFUSALS: Record<Exclude<AddResult, { ok: true }>["reason"], string> = {
  NOT_PERMITTED: "Your access does not include adding people to this household.",
  NO_PAY_PERMISSION:
    "You can add people, but not set what they are paid. Ask an owner to add them, or to give you that permission.",
  BAD_DETAILS: "Something in those details was not accepted. Check the name, phone number and rate.",
  DUPLICATE_PHONE: "Somebody in this household already has that mobile number.",
  NETWORK: "That did not reach us. Check your connection and try again.",
};

function hours(value: number): string {
  const whole = Math.floor(value);
  const mins = Math.round((value - whole) * 60);
  return mins === 0 ? `${whole}h` : `${whole}h ${String(mins).padStart(2, "0")}m`;
}

export function Employees({ ctx, onChanged, onOpen }: {
  ctx: EmployerView;
  onChanged: () => Promise<void>;
  /** Open one person's file. The name is the way in — a row that does
   *  nothing when clicked is a row people click twice. */
  onOpen: (employeeId: string) => void;
}) {
  const [may, setMay] = useState<{ people: boolean; pay: boolean }>({ people: false, pay: false });
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState<string | null>(null);

  // Whether this account may add somebody, and set what they are paid, is the
  // database's answer — asked, not assumed, so the button matches the outcome.
  useEffect(() => {
    let cancelled = false;
    mayAddPeople(ctx.orgId).then((result) => {
      if (!cancelled) setMay(result);
    });
    return () => {
      cancelled = true;
    };
  }, [ctx.orgId]);

  const week = ctx.week;
  const money = new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: ctx.currency,
    maximumFractionDigits: 0,
  });

  const active = ctx.employees.filter((e) => e.status === "active");
  const totals = [...week.values()].reduce(
    (sum, row) => ({
      total: sum.total + row.total,
      overtime: sum.overtime + row.overtime,
      wages: sum.wages + row.wages,
    }),
    { total: 0, overtime: 0, wages: 0 },
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>People</h1>
          <p>
            {ctx.orgName} — hours and wages this week so far, across{" "}
            {ctx.sites.length === 1 ? "one property" : `${ctx.sites.length} properties`}.
          </p>
        </div>
        {may.people && (
          <button className="btn inline" type="button" onClick={() => setAdding((v) => !v)}>
            {adding ? "Cancel" : "Add someone"}
          </button>
        )}
      </div>

      {added && <div className="notice ok">{added} has been added.</div>}

      {adding && (
        <AddSomeone
          ctx={ctx}
          canSetPay={may.pay}
          onDone={async (name) => {
            setAdding(false);
            setAdded(name);
            await onChanged();
          }}
        />
      )}

      <div className="panel">
        <div className="panel-top">
          <div className="panel-title">This week so far</div>
          <div className="panel-note">Since Monday</div>
        </div>
        <div className="panel-body">
          <div className="kpis">
            <div className="kpi">
              <small>On the books</small>
              <strong>{active.length}</strong>
            </div>
            <div className="kpi">
              <small>Hours recorded</small>
              <strong>{hours(totals.total)}</strong>
            </div>
            <div className="kpi">
              <small>Overtime</small>
              <strong>{hours(totals.overtime)}</strong>
            </div>
            <div className="kpi">
              <small>Wages so far</small>
              <strong>{money.format(totals.wages)}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-top">
          <div className="panel-title">Everyone</div>
          <div className="panel-note">
            Hours shown are shifts already closed off — anybody still on the clock is not counted
            yet.
          </div>
        </div>
        {ctx.employees.length === 0 ? (
          <div className="empty">
            Nobody has been added to {ctx.orgName} yet.
            <br />
            Add the first person and they can start clocking in.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="rows">
              <thead>
                <tr>
                  <th style={{ paddingLeft: 22 }}>Name</th>
                  <th>Rate</th>
                  <th>This week</th>
                  <th>Overtime</th>
                  <th>Wages</th>
                  <th style={{ paddingRight: 22 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {ctx.employees.map((employee) => {
                  const row = week.get(employee.id);
                  const terms = ctx.openTermFor(employee.id);
                  const site = ctx.siteOf(employee.siteId);
                  const ended = employee.status === "ended";
                  return (
                    <tr key={employee.id}>
                      <td style={{ paddingLeft: 22 }}>
                        <button
                          className="link-name"
                          type="button"
                          onClick={() => onOpen(employee.id)}
                        >
                          {employee.fullName}
                        </button>
                        <span className="sub">
                          {occupationLabel(employee.occupation, employee.occupationOther)}
                          {site ? ` · ${site.name}` : ""}
                        </span>
                      </td>
                      <td className="num">
                        {terms ? `${money.format(terms.rate)}/h` : <span className="pill warn">No terms</span>}
                      </td>
                      <td className="num">{hours(row?.total ?? 0)}</td>
                      <td className="num">
                        {row && row.overtime > 0 ? hours(row.overtime) : "—"}
                      </td>
                      <td className="num">{money.format(row?.wages ?? 0)}</td>
                      <td style={{ paddingRight: 22 }}>
                        {ended ? (
                          <span className="pill off">Ended</span>
                        ) : row?.onSite ? (
                          <span className="pill live">On the clock</span>
                        ) : (
                          <span className="pill ok">Active</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function AddSomeone({
  ctx,
  canSetPay,
  onDone,
}: {
  ctx: EmployerView;
  canSetPay: boolean;
  onDone: (name: string) => void | Promise<void>;
}) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [occupation, setOccupation] = useState<Occupation>("domestic");
  const [occupationOther, setOccupationOther] = useState("");
  const [siteId, setSiteId] = useState(ctx.sites[0]?.id ?? "");
  const [startedOn, setStartedOn] = useState(today);
  const [rate, setRate] = useState("");
  const [daysPerWeek, setDaysPerWeek] = useState(String(OCCUPATIONS.domestic.days));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const result = await addEmployee({
      orgId: ctx.orgId,
      siteId,
      fullName: fullName.trim(),
      phone: phone.trim(),
      occupation,
      occupationOther: occupation === "other" ? occupationOther.trim() : null,
      nationality: ctx.country,
      startedOn,
      rate: Number(rate),
      daysPerWeek: Number(daysPerWeek),
    });

    setBusy(false);
    if (result.ok) await onDone(fullName.trim());
    else setError(REFUSALS[result.reason]);
  }

  return (
    <div className="panel">
      <div className="panel-top">
        <div className="panel-title">Add someone</div>
        <div className="panel-note">
          They will be able to sign in on their phone and start clocking in.
        </div>
      </div>
      <div className="panel-body">
        {!canSetPay && (
          <div className="notice error">
            Your access does not include setting pay, so this will be refused. Ask an owner to add
            this person.
          </div>
        )}
        {error && <div className="notice error">{error}</div>}

        <form onSubmit={submit}>
          <div className="field-row">
            <div>
              <label htmlFor="fullName">Full name</label>
              <input
                id="fullName"
                type="text"
                required
                minLength={2}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="phone">Mobile number</label>
              <input
                id="phone"
                type="tel"
                required
                placeholder="082 123 4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>

          <div className="field-row">
            <div>
              <label htmlFor="occupation">Work</label>
              <select
                id="occupation"
                value={occupation}
                onChange={(e) => {
                  const next = e.target.value as Occupation;
                  setOccupation(next);
                  setDaysPerWeek(String(OCCUPATIONS[next].days));
                }}
              >
                {ALL_OCCUPATIONS.map((value) => (
                  <option key={value} value={value}>
                    {OCCUPATIONS[value].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              {occupation === "other" ? (
                <>
                  <label htmlFor="occupationOther">What is the work?</label>
                  <input
                    id="occupationOther"
                    type="text"
                    required
                    value={occupationOther}
                    onChange={(e) => setOccupationOther(e.target.value)}
                  />
                </>
              ) : (
                <>
                  <label htmlFor="site">Property</label>
                  <select id="site" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                    {ctx.sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
          </div>

          {occupation === "other" && (
            <>
              <label htmlFor="site-other">Property</label>
              <select id="site-other" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                {ctx.sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </>
          )}

          <div className="field-row">
            <div>
              <label htmlFor="rate">Rate an hour ({ctx.currency})</label>
              <input
                id="rate"
                type="number"
                required
                min="0.01"
                step="0.01"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="days">Days a week</label>
              <input
                id="days"
                type="number"
                required
                min="1"
                max="7"
                value={daysPerWeek}
                onChange={(e) => setDaysPerWeek(e.target.value)}
              />
            </div>
          </div>

          <label htmlFor="startedOn">First working day</label>
          <input
            id="startedOn"
            type="date"
            required
            value={startedOn}
            onChange={(e) => setStartedOn(e.target.value)}
          />

          <button className="btn" type="submit" disabled={busy || ctx.sites.length === 0}>
            {busy ? "Adding…" : "Add to the household"}
          </button>
          {ctx.sites.length === 0 && (
            <p className="auth-foot">
              This household has no property yet. Add one in the app under Setup before adding
              people — the clock needs somewhere to be.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
